import { Component, inject, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

/**
 * What the applicant chose, passed back through the modal result.
 *
 * `Reset` keeps the session and restarts the idle watch; `Cancel` signs out.
 * {@link SessionIdleService} maps these onto those two actions and is the only
 * place that performs either, so there is exactly one sign-out path.
 */
export type SessionIdleResult = 'Reset' | 'Cancel';

/**
 * Warning shown once the applicant has gone idle, counting down to automatic
 * sign-out.
 *
 * Opened by {@link SessionIdleService} when the idle clock enters its warning phase.
 * While it is open the service stops recording activity, so this resolves only on an
 * explicit button press or when the countdown reaches zero. That is what makes the
 * buttons reachable: were activity still counted, the mousemove or keypress needed to
 * reach one would itself count as activity and close the dialog before the click
 * lands.
 *
 * Ported from the internal app's `SessionIdleComponent`, converted to a standalone
 * component and following `ConfirmDialogComponent`'s conventions here.
 */
@Component({
	selector: 'app-session-idle',
	standalone: true,
	imports: [],
	template: `
		<div class="modal-header">
			<h2 class="modal-title h5" id="session-idle-title">Are you still there?</h2>
		</div>

		<div class="modal-body">
			<p class="mb-2">
				Your session will time out in
				<!--
					aria-hidden because this ticks every second. Announcing a per-second
					countdown is unusable with a screen reader; the live region below
					carries the same information at coarser thresholds.
				-->
				<span class="fw-semibold" aria-hidden="true">{{ display() }}</span>
			</p>

			<p class="visually-hidden" role="status" aria-live="polite" aria-atomic="true">{{ announcement() }}</p>

			<p class="mb-0">Choose <strong>I&rsquo;m still here</strong> to stay signed in. Anything you have already saved is safe.</p>
		</div>

		<div class="modal-footer">
			<button type="button" class="btn btn-outline-secondary" (click)="endSession()">End my session</button>
			<!-- Focus the non-destructive action; ng-bootstrap would otherwise focus "End my session". -->
			<button type="button" class="btn btn-primary" ngbAutofocus (click)="continueSession()">I&rsquo;m still here</button>
		</div>
	`,
})
export class SessionIdleComponent {
	private readonly modal = inject(NgbActiveModal);

	/** `m:ss`, for sighted users. Ticks every second, so it is hidden from assistive tech. */
	protected readonly display = signal('');

	/**
	 * Copy for the live region. Deliberately coarser than {@link display}: a polite
	 * live region bound to a value that changes every second either floods a screen
	 * reader or gets coalesced unpredictably, so it announces at whole minutes and
	 * then at 30 and 10 seconds. A signal only notifies on an actual value change,
	 * so each threshold is announced exactly once.
	 */
	protected readonly announcement = signal('');

	/**
	 * Push the remaining seconds in. Called imperatively by
	 * {@link SessionIdleService} on every tick of the warning phase — deliberately a
	 * method rather than an `@Input`, because nothing ever binds this in a template.
	 */
	setCountdown(secondsRemaining: number): void {
		const seconds = Math.max(0, secondsRemaining);
		this.display.set(`${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`);
		this.announcement.set(announcementFor(seconds));
	}

	/** Stay signed in. */
	protected continueSession(): void {
		this.resolve('Reset');
	}

	/** Sign out now instead of waiting out the countdown. */
	protected endSession(): void {
		this.resolve('Cancel');
	}

	private resolve(result: SessionIdleResult): void {
		this.modal.close(result);
	}
}

/** Thresholds, narrowest first so the closest match wins. */
function announcementFor(seconds: number): string {
	if (seconds <= 10) {
		return 'Your session will time out in 10 seconds.';
	}
	if (seconds <= 30) {
		return 'Your session will time out in 30 seconds.';
	}
	const minutes = Math.ceil(seconds / 60);
	return `Your session will time out in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`;
}
